/**
 * Admin → Location Service: one hub for the tenant's switched-on Location Service Engine modules. Only the tabs
 * for modules the Super Admin enabled are listed (the server also refuses the others with 403).
 */
import { NavLink, Outlet } from "react-router-dom";
import { ArrowRight, MapPinned } from "lucide-react";
import { useLse } from "../../../lib/lse";

export const LSE_TABS = [
    { to: "zones", label: "Zones & delivery", any: ["zone_types", "zone_products", "instant_delivery", "express_delivery", "scheduled_delivery", "next_day_delivery", "warehouses"] },
    { to: "warehouses", label: "Warehouses & stock", any: ["warehouses"] },
    { to: "picking", label: "Picking", any: ["warehouse_picking"] },
    { to: "routes", label: "Villages & routes", any: ["route_batching"] },
    { to: "dispatch", label: "Route dispatch", any: ["route_batching"] },
    { to: "refunds", label: "Refunds", any: ["refund_management"] },
    { to: "support", label: "Support", any: ["customer_support"] },
    { to: "offers", label: "Area offers", any: ["zone_offers", "zone_products", "rating_aggregation"] },
    { to: "messages", label: "Zone messages", any: ["zone_notifications"] },
    { to: "analytics", label: "Analytics", any: ["rural_analytics", "partner_analytics"] },
    { to: "audit", label: "Audit log", any: ["audit_log"] },
    { to: "settings", label: "Settings", any: ["delivery_otp", "delivery_photo_proof", "delivery_gps_verification", "route_batching", "email_notifications"] },
];

export function useLseTabs() {
    const { has, on } = useLse();
    return on ? LSE_TABS.filter((t) => t.any.some(has)) : [];
}

export default function LseLayout() {
    const { on } = useLse();
    const tabs = useLseTabs();
    if (!on) {
        return (
            <div className="bg-card border border-dashed border-border rounded-2xl p-8 text-center text-sm text-muted-foreground" data-testid="lse-off">
                Location Service isn't enabled for this store. Ask your platform administrator.
            </div>
        );
    }
    return (
        <div data-testid="lse-hub" className="min-w-0">
            <h1 className="text-2xl font-bold inline-flex items-center gap-2"><MapPinned size={22} className="text-primary" /> Location Service</h1>
            <nav className="mt-3 -mx-1 px-1 flex gap-2 overflow-x-auto pb-1 no-scrollbar" aria-label="Location Service sections">
                <NavLink end to="/admin/lse" className={({ isActive }) => `shrink-0 rounded-full px-3 py-1.5 text-xs font-semibold border ${isActive ? "bg-foreground text-background border-foreground" : "border-border"}`}>Overview</NavLink>
                {tabs.map((t) => (
                    <NavLink key={t.to} to={`/admin/lse/${t.to}`} data-testid={`lse-tab-${t.to}`}
                             className={({ isActive }) => `shrink-0 rounded-full px-3 py-1.5 text-xs font-semibold border ${isActive ? "bg-foreground text-background border-foreground" : "border-border"}`}>
                        {t.label}
                    </NavLink>
                ))}
            </nav>
            <div className="mt-5 min-w-0"><Outlet /></div>
        </div>
    );
}

export function LseOverview() {
    const { modules } = useLse();
    const tabs = useLseTabs();
    return (
        <div data-testid="lse-overview">
            <p className="text-sm text-muted-foreground max-w-2xl">
                {modules.size} capabilit{modules.size === 1 ? "y is" : "ies are"} switched on for your store. Each section below manages one of them.
            </p>
            <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                {tabs.map((t) => (
                    <NavLink key={t.to} to={`/admin/lse/${t.to}`} className="bg-card border border-border rounded-2xl p-4 flex items-center justify-between gap-3 hover:border-primary">
                        <span className="font-semibold text-sm">{t.label}</span>
                        <ArrowRight size={16} className="text-muted-foreground" />
                    </NavLink>
                ))}
            </div>
            <p className="mt-6 text-[11px] uppercase tracking-wider font-bold text-muted-foreground">Active modules</p>
            <div className="mt-2 flex flex-wrap gap-1.5">
                {[...modules].map((m) => <span key={m} className="text-[11px] font-semibold rounded-full bg-primary/10 text-primary px-2 py-0.5">{m.replace(/_/g, " ")}</span>)}
            </div>
        </div>
    );
}
